<template>
  <div class="mobileCardWrapper">
    <div class="header">
      <div class="headerContent">
        <span class="headerTitle">{{ item?.dispositif }}</span>
        <span class="headerSubtitle">{{ item?.code }}</span>
        <BasicBadge
          :type="StatutBadgeType[itemStatut]"
          size="medium"
          :deletable="false"
          :label="StatutLibelle[itemStatut]"
        />
      </div>
      <div class="actionsWrapper">
        <BasicTooltip
          v-if="stagiairesList.length"
          label="Afficher le détail de la demande"
          position="right"
        >
          <div
            v-if="stagiairesList.length"
            v-focusable="{
              focusable: true,
              onEnter: () => toggleModal,
            }"
            class="elem elem--span-1 elem--clickable"
            @click="toggleModal"
          >
            <BasicText size="h3">
              <div class="text">
                <span>...</span>
              </div>
            </BasicText>
          </div>
        </BasicTooltip>
        <div
          v-if="item.niveauPermissionDr === 'Ecriture'"
          v-focusable="{ onEnter: () => addDemandeDR(item) }"
          class="elem elem--span-1 elem--clickable"
        >
          <BasicTooltip label="Créer une demande de remboursement">
            <BasicIcon
              name="add"
              :active="false"
              @click="addDemandeDR(item)"
            />
          </BasicTooltip>
        </div>
        <div
          v-focusable="{
            onEnter: () => displayDossier(item?.code as string),
          }"
          class="elem elem--span-1 elem--clickable"
        >
          <BasicTooltip label="Consulter la demande">
            <BasicIcon
              name="eye"
              :active="false"
              @click="displayDossier(item?.code as string)"
            />
          </BasicTooltip>
        </div>
      </div>
    </div>
    <div class="content">
      <div class="contentElement">
        <BasicIcon
          name="document-text"
          :active="false"
        />
        <span>{{ item?.dispositif }}</span>
      </div>
      <div class="contentElement">
        <BasicIcon
          name="teacher"
          :active="false"
        />
        <span>{{ item?.intitule }}</span>
      </div>
      <div class="contentElement">
        <BasicIcon
          name="bank"
          :active="false"
        />
        <span>{{ item?.etablissementAdherentPrincipal?.denomination }}</span>
      </div>
      <div
        class="contentElement"
        v-if="item.estSousMandatGestion && appState.contextCourant === 2"
      >
        <BasicIcon
          name="document-text"
          active
        />
        <span>Mandat de gestion</span>
      </div>
      <div
        class="contentElement"
        v-if="item.avecSubrogation"
      >
        <BasicIcon
          name="wallet"
          :active="false"
        />
        <span>Subrogation de paiement</span>
      </div>
    </div>
    <div class="footer">
      <div class="footerElem">
        <span class="footerTitle">Début</span>
        <span class="footerSubtitle">{{ dateFormatter(item?.dateDebut) }}</span>
      </div>
      <div class="footerElem">
        <span class="footerTitle">Fin</span>
        <span class="footerSubtitle">{{ dateFormatter(item?.dateFin) }}</span>
      </div>
      <div class="footerElem">
        <span class="footerTitle">Stagiaire(s)</span>
        <span
          v-if="stagiaires"
          class="footerSubtitle footerSubtitle--clickable"
          @click="toggleModal()"
        >
          {{ stagiaires }}
        </span>
        <span
          v-else
          class="footerSubtitle"
        >
          -
        </span>
      </div>
    </div>
  </div>
  <Teleport to="main">
    <ModalComponent v-model="showModal">
      <template #header>Liste des stagiaires</template>
      <template #content>
        <BasicText
          color="neutral-500"
          size="body-m"
        >
          {{ stagiairesList }}
        </BasicText>
      </template>
    </ModalComponent>
  </Teleport>
</template>

<script setup lang="ts">
  import type { RechercheResultDTO } from '@/features/api/extranet/dto/Dossier/Recherche'
import { useAppStateStore } from '@/features/application'
import { type Statut, StatutBadgeType, StatutLibelle } from '@/features/domaine/types/opco/Dossier'
import ModalComponent from '@/features/interface/modal/ModalComponent.vue'
import { dateFormatter } from '@/features/shared/tools/date'
import { computed, ref } from 'vue'
import { useRechercheDossierTtActions } from '../useRechercheDossierTtActions'

  const { displayDossier, addDemandeDR } = useRechercheDossierTtActions()

  const appState = useAppStateStore()
  const props = defineProps<{ item: RechercheResultDTO }>()
  const showModal = ref(false)

  const stagiaires = computed(() => {
    const stagiaires = props.item.stagiaires
    if (!stagiaires || stagiaires.length === 0) return ''
    const first = stagiaires[0]?.prenom + ' ' + stagiaires[0]?.nom
    const others = stagiaires.length - 1
    return first + (others > 0 ? ` + ${others}` : '')
  })

  const stagiairesList = computed(() => {
    const stagiaires = props.item?.stagiaires
    if (!stagiaires || stagiaires.length === 0) return []

    return stagiaires
      .map((stagiaire) => {
        return (stagiaire.prenom ?? '') + ' ' + (stagiaire.nom ?? '')
      })
      .join(', ')
  })

  const itemStatut = computed<Statut>(() => {
    if (props.item?.statut) {
      return props.item?.statut as Statut
    }
    return 'Transmis'
  })

  const toggleModal = () => {
    showModal.value = true
  }
</script>

<style scoped>
  @import '../../index.less';
  @import './DossierCardMobile.less';
</style>
